import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { EmptyState } from "@/components/empty-state"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { OrderStatusBadge } from "@/features/orders/components/order-status-badge"
import { useCooperatives } from "@/features/cooperatives/hooks/use-cooperatives"
import { kgToMt, formatNumber } from "@/lib/format"
import { paths } from "@/routes/paths"
import type { Contract } from "@/types/domain"

interface ContractOrdersTableProps {
  contract: Contract
}

export function ContractOrdersTable({ contract }: ContractOrdersTableProps) {
  const { t } = useTranslation(["contracts", "common"])
  const navigate = useNavigate()
  const { data: cooperatives } = useCooperatives()
  const orders = contract.Orders ?? []

  function nameForGroup(groupId: number | string | null | undefined) {
    if (!groupId) return ""
    const match = (cooperatives ?? []).find(
      (c) => String(c.id) === String(groupId),
    )
    return match?.name ?? ""
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("orders.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        {orders.length === 0 ? (
          <EmptyState title={t("orders.table.empty")} />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("orders.table.cooperative")}</TableHead>
                  <TableHead>{t("orders.table.quantity")}</TableHead>
                  <TableHead>{t("orders.table.status")}</TableHead>
                  <TableHead>{t("orders.table.purchases")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => {
                  const totalMt = kgToMt(order.agreedQuantity ?? 0)
                  const lockedDeliveries = (order.DeliveryForms ?? []).filter(
                    (d) => d.isLocked,
                  ).length
                  return (
                    <TableRow
                      key={String(order.id)}
                      className="cursor-pointer"
                      onClick={() => navigate(paths.orderDetail(order.id))}
                    >
                      <TableCell className="font-medium">
                        {nameForGroup(order.groupId) || order.orderNumber || "—"}
                      </TableCell>
                      <TableCell>
                        {formatNumber(totalMt)} {t("units.mt", { ns: "common" })}
                      </TableCell>
                      <TableCell>
                        <OrderStatusBadge status={order.orderStatus ?? null} />
                      </TableCell>
                      <TableCell>{lockedDeliveries}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
